import React, { useEffect, useState } from 'react';
import { Search, X, Plus, Filter } from 'lucide-react';
import { useStore, Recipe, Meal } from '../../store/useStore';
import CreateMealModal from './CreateMealModal';

interface AddMealModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  mealType: string;
  onAddMeal: (recipe: Recipe[]) => void;
  selected?: Meal[];
}

const AddMealModal: React.FC<AddMealModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
  mealType,
  onAddMeal,
  selected
}) => {
  const recipes = useStore((state) => state.recipes);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipes, setSelectedRecipes] = useState<Recipe[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [categories, setCategories] = useState(['All', 'Breakfast', 'Main Course', 'Salad', 'Dessert', 'Snack']);
  const [difficulties, setDifficulties] = useState(['All', 'Easy', 'Medium', 'Hard']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [showFavorites, setShowFavorites] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const user = useStore((state) => state.user);

  // const filteredRecipes = recipes.filter(recipe =>
  //   recipe.title.toLowerCase().includes(searchTerm.toLowerCase())
  // );

  const filteredRecipes = recipes.filter(recipe => {
    // For custom recipes (ones with userId), only show if they belong to the current user
    // if (recipe.userId && recipe.userId !== user?.id) return false;

    // Filter by subscription status for variations
    // if (!subscription && recipe.isVariation) return false;

    const matchesSearch = recipe.title.toLowerCase().includes(searchTerm.toLowerCase());
    // console.log(matchesSearch, )
    const matchesCategory = selectedCategory === 'All' || recipe.category.includes(selectedCategory);
    const matchesDifficulty = selectedDifficulty === 'All' || recipe.difficulty === selectedDifficulty;
    const matchesFavorites = !showFavorites || recipe.isFavorite;

    // console.log(matchesSearch, matchesCategory,matchesDifficulty, matchesFavorites);
    return matchesSearch && matchesCategory && matchesDifficulty && matchesFavorites;
  });

  const toggleRecipeSelection = (recipe: Recipe) => {
    setSelectedRecipes(prev => {
      const isSelected = prev.some(r => r.id === recipe.id);
      if (isSelected) {
        return prev.filter(r => r.id !== recipe.id);
      } else {
        return [...prev, recipe];
      }
    });
  };

  const handleAddMeals = () => {
    // selectedRecipes.forEach(recipe => {
    onAddMeal(selectedRecipes);
    // });
    onClose();
  };

  const handleCreateMeal = (recipe: Recipe) => {
    setSelectedRecipes([...selectedRecipes, recipe]);
    setShowCreateModal(false);
  };

  if (!isOpen) return null;

  useEffect(() => {
    try {
      const uniqueCategories = [
        ...new Set(recipes.flatMap(recipe => recipe?.category || []))
      ];
      const uniqueDifficulties = [
        ...new Set(recipes.flatMap(recipe => recipe?.difficulty || []))
      ];

      console.log("Selected recipes: ", selected);
      let selectedRecipes: any[] = [];
      if (selected) {
        selectedRecipes = selected
          .map((item: any) => recipes.find((recipe) => recipe.id === item.recipeId))
          .filter((recipe) => recipe !== undefined);
      }
      console.log("Selected recipes: ", selectedRecipes);
      setSelectedRecipes(selectedRecipes);

      setCategories(['All', ...uniqueCategories]);
      setDifficulties(['All', ...uniqueDifficulties]);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  }, []);

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
          <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                Add Meals for{' '}
                {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mt-6 flex justify-between items-center gap-4">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 px-4 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Filter size={18} className="mr-2" />
                Filters
              </button>
            </div>
            {!showFilters && (<div className='cursor-pointer mt-2 mb-2 text-secondary hover:text-secondary/90' onClick={() => setShowOnlySelected(!showOnlySelected)}>
              {showOnlySelected ? 'Show All' : 'Show only selected'}
            </div>)}
            {showFilters && (<div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 space-y-4 animate-fadeIn">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-1.5 rounded-full text-sm ${selectedCategory === category
                        ? 'bg-secondary text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Difficulty</label>
                <div className="flex flex-wrap gap-2">
                  {difficulties.map(difficulty => (
                    <button
                      key={difficulty}
                      onClick={() => setSelectedDifficulty(difficulty)}
                      className={`px-3 py-1.5 rounded-full text-sm ${selectedDifficulty === difficulty
                        ? 'bg-secondary text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                      {difficulty}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Other Filters</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowFavorites(!showFavorites)}
                    className={`px-3 py-1.5 rounded-full text-sm ${showFavorites
                      ? 'bg-secondary text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                  >
                    Favourites Only
                  </button>
                </div>
              </div>
            </div>)}
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {showOnlySelected && selectedRecipes.map((recipe) => {
                return (
                  <div
                    key={recipe.id}
                    onClick={() => toggleRecipeSelection(recipe)}
                    className={`cursor-pointer rounded-lg border-secondary ring-1 ring-secondary border 
                      overflow-hidden hover:border-secondary transition-colors`}>
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-3">
                      <h4 className="font-medium text-gray-800 dark:text-white">{recipe.title}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {recipe.prepTime} min prep · {recipe.cookTime} min cook
                      </p>
                      <div className="flex items-center mt-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${recipe.difficulty === 'Easy'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : recipe.difficulty === 'Medium'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          }`}>
                          {recipe.difficulty}
                        </span>
                        {Array.isArray(recipe.category) && recipe.category.map((each) => <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                          {each}
                        </span>)}
                      </div>
                    </div>
                  </div>
                );
              })
              }

              {!showOnlySelected && filteredRecipes.map((recipe) => {
                const isSelected = selectedRecipes.some(r => r.id === recipe.id);
                return (
                  <div
                    key={recipe.id}
                    onClick={() => toggleRecipeSelection(recipe)}
                    className={`cursor-pointer rounded-lg border ${isSelected
                      ? 'border-secondary ring-1 ring-secondary'
                      : 'border-gray-200 dark:border-gray-700'
                      } overflow-hidden hover:border-secondary transition-colors`}
                  >
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-3">
                      <h4 className="font-medium text-gray-800 dark:text-white">{recipe.title}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {recipe.prepTime} min prep · {recipe.cookTime} min cook
                      </p>
                      <div className="flex items-center mt-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${recipe.difficulty === 'Easy'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : recipe.difficulty === 'Medium'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          }`}>
                          {recipe.difficulty}
                        </span>
                        {Array.isArray(recipe.category) && recipe.category.map((each) => <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                          {each}
                        </span>)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {!showOnlySelected && filteredRecipes.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No recipes found matching your search.</p>
              </div>
            )}

            {showOnlySelected && selectedRecipes.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No recipes selected.</p>
              </div>
            )}
          </div>

          <div className="flex-shrink-0 p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleAddMeals}
              disabled={selectedRecipes.length === 0}
              className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(selected?.length ?? 0) > 0 ? 'Update' : `Add ${selectedRecipes.length} ${selectedRecipes.length === 1 ? 'Meal' : 'Meals'}`}
            </button>
          </div>
        </div>
      </div>

      <CreateMealModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateMeal={handleCreateMeal}
      />
    </>
  );
};

export default AddMealModal;